import { cva } from "class-variance-authority";
import React from "react";
import { match } from "ts-pattern";
import { BaseButton } from "@/components/atomic/BaseButton";
import { cn } from "@/lib/utils";

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  variant?: "primary" | "activated" | "secondary" | "outline" | "readmore";
  size?: "default" | "sm" | "lg";
}

const buttonVariants = cva(
  "gap-2 cursor-pointer shadow-[0px_2px_#3C569D] text-white font-bold rounded-[5px]",
  {
    variants: {
      variant: {
        primary: "px-8 bg-[#FFE46E] hover:bg-[#FFE46E] text-gray-900 shadow-[0px_2px_#9A8839]",
        secondary: "bg-[#6888E1] hover:bg-[#6888E1] rounded-[10px] shadow-[0px_4px_#3C569D]",
        activated:
          "bg-[#293D72] hover:bg-[#293D72] inset-shadow-[0px_2px_#233462] shadow-[0px_4px_#293D72] rounded-[10px]",
        outline:
          "gap-1 border-0 border-input bg-background shadow-[0px_0px_2px_#00000050] hover:bg-accent text-[#5F6368] font-normal",
        readmore:
          "w-[200px] min-h-[40px] px-16 py-2 gap-2.5 rounded-[20px] border border-[#B7B7B7] bg-gradient-to-b from-white to-[#E5E5E5] text-[#333333] font-bold shadow-none text-[12px]",
      },
      size: {
        default: "h-[30px] px-2",
        sm: "h-[22px] px-2 text-xs",
        lg: "text-lg px-2",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

const Button = React.forwardRef<HTMLButtonElement, Props>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <BaseButton
        className={cn(
          buttonVariants({ variant, size, className }),
          className,
        )}
        ref={ref}
        variant={match(variant).with("outline", (v) => v).otherwise(() => "default")}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
export default Button;
